import { useState, useEffect } from 'react'
import {
  AlertCircle,
  CheckCircle2,
  ChevronRight,
  Clipboard,
  Code2,
  FileText,
  Loader2,
  X,
} from 'lucide-react'
import { INGESTION_API, fetchApi } from '@/api/client'
import { FieldMappingTable } from './FieldMappingTable'
import type { MappingRow } from './FieldMappingTable'
import type {
  ContentType,
  DataService,
  IngestCredentials,
  IngestResponse,
  SchemaField,
} from '../types'

// ── Props ───────────────────────────────────────────────────────────────────
interface Props {
  initialFilePath?: string
  initialBucketName?: string
  initialContentType?: ContentType
  csvHeaders?: string[]
  initialDataTypes?: Record<string, string>
  credentials: IngestCredentials
  demoMode?: boolean
  onResult: (result: IngestResponse) => void
}

// ── Helpers ──────────────────────────────────────────────────────────────────

const VALID_TYPES = ['string', 'number', 'date', 'boolean'] as const

/** Map any API-returned type to one of the 4 canonical dropdown values. */
function normalizeType(v: string | undefined): string {
  const lower = v?.toLowerCase().trim() ?? ''
  if (lower === 'number' || lower === 'integer' || lower === 'int' ||
      lower === 'float' || lower === 'decimal' || lower === 'double' || lower === 'numeric') return 'number'
  if (lower === 'date' || lower === 'datetime' || lower === 'timestamp') return 'date'
  if (lower === 'boolean' || lower === 'bool') return 'boolean'
  if (VALID_TYPES.includes(lower as typeof VALID_TYPES[number])) return lower
  return 'string'
}

function normalizeDataTypes(types: Record<string, string>): Record<string, string> {
  return Object.fromEntries(Object.entries(types).map(([k, v]) => [k, normalizeType(v)]))
}

// ── Component ───────────────────────────────────────────────────────────────
export function Step3IngestForm({
  initialFilePath = '',
  initialBucketName = '',
  csvHeaders = [],
  initialDataTypes,
  credentials,
  demoMode,
  onResult,
}: Props) {
  // File info
  const [filePath, setFilePath] = useState(initialFilePath)
  const [bucketName, setBucketName] = useState(initialBucketName)
  const [service, setService] = useState<DataService>('gcp')
  const [timezone, setTimezone] = useState('Asia/Kolkata')
  const [reqSource, setReqSource] = useState('admin_portal')

  useEffect(() => { if (initialFilePath) setFilePath(initialFilePath) }, [initialFilePath])
  useEffect(() => { if (initialBucketName) setBucketName(initialBucketName) }, [initialBucketName])

  // Entity selection
  const [availableEntities, setAvailableEntities] = useState<string[]>([])
  const [entitiesLoading, setEntitiesLoading] = useState(false)
  const [selectedEntity, setSelectedEntity] = useState<string>('customer')

  // Schema fields for selected entity
  const [schemaFields, setSchemaFields] = useState<SchemaField[]>([])
  // Customer schema fields (for "customer mapping" column in entity mode)
  const [customerSchemaFields, setCustomerSchemaFields] = useState<SchemaField[]>([])
  const [schemasLoading, setSchemasLoading] = useState(false)

  // Entity-specific metadata
  const [entityIdCol, setEntityIdCol] = useState('')

  // Unified mapping rows
  const [mappingRows, setMappingRows] = useState<MappingRow[]>([])

  // Local CSV headers — seeded from prop, updatable via cloud detection
  const [localCsvHeaders, setLocalCsvHeaders] = useState<string[]>(csvHeaders)
  const [detectedDataTypes, setDetectedDataTypes] = useState<Record<string, string> | null>(null)
  const [headerFetchLoading, setHeaderFetchLoading] = useState(false)
  const [headerFetchError, setHeaderFetchError] = useState<string | null>(null)

  // Keep in sync if parent updates headers (e.g. from step 2)
  useEffect(() => { if (csvHeaders.length > 0) setLocalCsvHeaders(csvHeaders) }, [csvHeaders])

  // Seed data types from parent when coming from Step 2
  useEffect(() => {
    if (initialDataTypes && Object.keys(initialDataTypes).length > 0) {
      setDetectedDataTypes(normalizeDataTypes(initialDataTypes))
    }
  }, [initialDataTypes])

  // When headers arrive with no types yet, default all columns to "string"
  useEffect(() => {
    if (localCsvHeaders.length > 0) {
      setDetectedDataTypes((prev) => {
        if (prev && Object.keys(prev).length > 0) return prev
        return Object.fromEntries(localCsvHeaders.map((h) => [h, 'string']))
      })
    }
  }, [localCsvHeaders])

  // Submit / error / UI
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [customerCallResult, setCustomerCallResult] = useState<IngestResponse | null>(null)
  const [showPreview, setShowPreview] = useState(false)
  const [pasteMode, setPasteMode] = useState(false)
  const [pasteJson, setPasteJson] = useState('')

  const apiHeaders = () => ({
    'Content-Type': 'application/json',
    'X-FH-Key-ID': credentials.keyId,
    'X-FH-Key-Secret': credentials.secret,
  })

  const handleFetchHeaders = async () => {
    if (!filePath || !bucketName) return
    setHeaderFetchLoading(true)
    setHeaderFetchError(null)
    try {
      const res = await fetch(`${INGESTION_API}/upload/file-headers`, {
        method: 'POST',
        headers: apiHeaders(),
        body: JSON.stringify({ service, bucket: bucketName, filePath }),
      })
      const data = await res.json()
      if (!res.ok) throw new Error(data.message || `HTTP ${res.status}`)
      if (data.headers?.length) {
        setLocalCsvHeaders(data.headers)
        // Always populate types table: normalize + use inferred types or default all to "string"
        setDetectedDataTypes(
          data.dataTypes && Object.keys(data.dataTypes).length > 0
            ? normalizeDataTypes(data.dataTypes)
            : Object.fromEntries((data.headers as string[]).map((h: string) => [h, 'string']))
        )
      } else {
        setHeaderFetchError('No headers found in file.')
      }
    } catch (err) {
      setHeaderFetchError(err instanceof Error ? err.message : 'Failed to fetch headers')
    } finally {
      setHeaderFetchLoading(false)
    }
  }

  // Updates a single column's type and keeps paste JSON in sync if open
  const handleDataTypeChange = (header: string, type: string) => {
    const newTypes = { ...(detectedDataTypes ?? {}), [header]: type }
    setDetectedDataTypes(newTypes)
    if (pasteMode && pasteJson) {
      try {
        const parsed = JSON.parse(pasteJson)
        parsed.data_types = newTypes
        setPasteJson(JSON.stringify(parsed, null, 2))
      } catch { /* invalid JSON — leave it */ }
    }
  }

  // ── Load available entities ───────────────────────────────────────────────
  useEffect(() => {
    loadEntities()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const loadEntities = async () => {
    if (demoMode) {
      setAvailableEntities(['customer', 'policy', 'member', 'coverage', 'asset'])
      return
    }
    setEntitiesLoading(true)
    try {
      const data = await fetchApi<{ entities: string[] }>(
        `${INGESTION_API}/schemas/list`,
        { headers: apiHeaders() },
      )
      if (data.entities?.length) {
        setAvailableEntities(data.entities)
        setSelectedEntity(data.entities[0])
      }
    } catch {
      setAvailableEntities(['customer'])
    } finally {
      setEntitiesLoading(false)
    }
  }

  // ── Load schemas when entity changes ─────────────────────────────────────
  useEffect(() => {
    if (selectedEntity) {
      loadSchemaForEntity(selectedEntity)
      setMappingRows([])
      setEntityIdCol('')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEntity])

  // Always keep customer schema loaded (needed for entity customer-mapping column)
  useEffect(() => {
    if (selectedEntity !== 'customer') {
      loadCustomerSchema()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEntity])

  const loadSchemaForEntity = async (entity: string) => {
    if (demoMode) {
      setSchemaFields(entity === 'customer' ? DEMO_CUSTOMER_FIELDS : DEMO_ENTITY_FIELDS)
      return
    }
    setSchemasLoading(true)
    try {
      const data = await fetchApi<{ fields?: SchemaField[] }>(
        `${INGESTION_API}/schemas/${entity}`,
        { headers: apiHeaders() },
      )
      setSchemaFields(data.fields ?? [])
    } catch {
      setSchemaFields([])
    } finally {
      setSchemasLoading(false)
    }
  }

  const loadCustomerSchema = async () => {
    if (demoMode) { setCustomerSchemaFields(DEMO_CUSTOMER_FIELDS); return }
    try {
      const data = await fetchApi<{ fields?: SchemaField[] }>(
        `${INGESTION_API}/schemas/customer`,
        { headers: apiHeaders() },
      )
      setCustomerSchemaFields(data.fields ?? [])
    } catch {
      setCustomerSchemaFields([])
    }
  }

  const isCustomer = selectedEntity === 'customer'
  const SYSTEM_ENTITIES = ['transaction', 'transaction_item']
  const isSystemEntity = SYSTEM_ENTITIES.includes(selectedEntity)

  // ── Payload generation ────────────────────────────────────────────────────
  const generatePayload = (): Record<string, unknown> => {
    const payload: Record<string, unknown> = {
      file_path: filePath,
      bucket_name: bucketName,
      content_type: isCustomer ? 'customer' : 'entity',
      service,
      timezone: timezone || 'Asia/Kolkata',
      req_source: reqSource || 'admin_portal',
    }

    // Include data types detected from file-headers if available (used as fallback by backend)
    if (detectedDataTypes && Object.keys(detectedDataTypes).length > 0) {
      payload.data_types = detectedDataTypes
    }

    const mapped = mappingRows.filter((r) => r.csvHeader && r.schemaField)

    // uni_params: rows with isUni checked
    const uniRows = mapped.filter((r) => r.isUni)
    if (uniRows.length) {
      payload.uni_params = Object.fromEntries(uniRows.map((r) => [r.csvHeader, r.uniValue]))
    }

    if (isCustomer) {
      // customer_field_mappings: all mapped rows
      const cfm = Object.fromEntries(mapped.map((r) => [r.csvHeader, r.schemaField]))
      if (Object.keys(cfm).length) payload.customer_field_mappings = cfm
    } else {
      // entity metadata
      if (isSystemEntity) {
        payload.entity_type = 'system'
        payload.entity_name = selectedEntity
      } else {
        payload.entity_type = selectedEntity
      }
      if (entityIdCol) payload.entity_id = entityIdCol

      // entity_field_mappings: all mapped rows (Cust checkbox only adds to customer_field_mappings, doesn't remove from entity)
      const entityRows = mapped
      if (entityRows.length) {
        payload.entity_field_mappings = Object.fromEntries(
          entityRows.map((r) => [r.csvHeader, r.schemaField]),
        )
      }

      // customer_field_mappings: customer-mapping rows (uses customerField value)
      const custRows = mappingRows.filter((r) => r.isCustomerMapping && r.csvHeader && r.customerField)
      if (custRows.length) {
        payload.customer_field_mappings = Object.fromEntries(
          custRows.map((r) => [r.csvHeader, r.customerField]),
        )
      }
    }

    return payload
  }

  const switchToPasteMode = () => {
    setPasteJson(JSON.stringify(generatePayload(), null, 2))
    setPasteMode(true)
    setShowPreview(false)
  }

  // ── Submit ────────────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    setLoading(true)
    setError(null)
    setCustomerCallResult(null)
    try {
      let payload: Record<string, unknown>
      if (pasteMode) {
        try {
          payload = JSON.parse(pasteJson)
        } catch {
          setError('Invalid JSON — please fix the syntax before submitting.')
          return
        }
      } else {
        payload = generatePayload()
      }

      if (demoMode) {
        await new Promise((r) => setTimeout(r, 800))
        onResult({
          status: 'ACCEPTED',
          message: 'File accepted for processing (demo mode)',
          request_id: 'demo-req-' + Math.random().toString(36).slice(2, 10),
          validation_summary: { headers_validated: localCsvHeaders.length || 8, total_rows_in_file: 120 },
        })
        return
      }

      const custMappings = payload.customer_field_mappings as Record<string, string> | undefined
      const entityMappings = payload.entity_field_mappings as Record<string, string> | undefined
      const isDualCall =
        custMappings && Object.keys(custMappings).length > 0 &&
        entityMappings && Object.keys(entityMappings).length > 0

      if (isDualCall) {
        // Entity call — strip customer_field_mappings
        const { customer_field_mappings: _cfm, ...entityPayload } = payload

        // Customer call — keep shared fields only, set content_type to customer
        const customerPayload: Record<string, unknown> = {
          file_path: payload.file_path,
          bucket_name: payload.bucket_name,
          content_type: 'customer',
          service: payload.service,
          timezone: payload.timezone,
          req_source: payload.req_source,
          ...(payload.data_types ? { data_types: payload.data_types } : {}),
          ...(payload.uni_params ? { uni_params: payload.uni_params } : {}),
          customer_field_mappings: custMappings,
        }

        const [entityRes, customerRes] = await Promise.all([
          fetch(`${INGESTION_API}/ingest`, {
            method: 'POST',
            headers: apiHeaders(),
            body: JSON.stringify(entityPayload),
          }),
          fetch(`${INGESTION_API}/ingest`, {
            method: 'POST',
            headers: apiHeaders(),
            body: JSON.stringify(customerPayload),
          }),
        ])

        const entityData: IngestResponse = await entityRes.json()
        const customerData: IngestResponse = await customerRes.json()

        onResult(entityData)
        setCustomerCallResult(customerData)
      } else {
        const res = await fetch(`${INGESTION_API}/ingest`, {
          method: 'POST',
          headers: apiHeaders(),
          body: JSON.stringify(payload),
        })
        const data: IngestResponse = await res.json()
        onResult(data)
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Request failed')
    } finally {
      setLoading(false)
    }
  }

  const hasDataTypes = Boolean(detectedDataTypes && Object.keys(detectedDataTypes).length > 0)
  const canSubmit = pasteMode
    ? Boolean(pasteJson.trim())
    : Boolean(filePath && bucketName && selectedEntity && hasDataTypes)

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="space-y-5">
      {/* Mode toggle */}
      <div className="flex items-center justify-between">
        <p className="text-xs text-gray-500">
          Configure the ingestion request, or paste a JSON payload directly.
        </p>
        <div className="flex gap-2">
          {pasteMode ? (
            <>
              <button
                type="button"
                onClick={() => setPasteJson(JSON.stringify(generatePayload(), null, 2))}
                className="text-xs text-gray-500 hover:text-gray-700 flex items-center gap-1"
              >
                <Clipboard className="h-3.5 w-3.5" />
                Re-generate from form
              </button>
              <button
                type="button"
                onClick={() => { setPasteMode(false); setPasteJson('') }}
                className="text-xs text-primary hover:underline flex items-center gap-1"
              >
                <ChevronRight className="h-3.5 w-3.5" />
                Switch to Form
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                onClick={() => setShowPreview((v) => !v)}
                className="text-xs text-gray-500 hover:text-gray-700 flex items-center gap-1"
              >
                <Code2 className="h-3.5 w-3.5" />
                {showPreview ? 'Hide Preview' : 'Preview JSON'}
              </button>
              <button
                type="button"
                onClick={switchToPasteMode}
                className="text-xs text-primary hover:underline flex items-center gap-1"
              >
                <Clipboard className="h-3.5 w-3.5" />
                Paste JSON
              </button>
            </>
          )}
        </div>
      </div>

      {/* ── Paste JSON mode ─────────────────────────────────────────────────── */}
      {pasteMode && (
        <textarea
          value={pasteJson}
          onChange={(e) => setPasteJson(e.target.value)}
          rows={20}
          spellCheck={false}
          className="w-full font-mono text-xs px-3 py-3 border border-gray-300 rounded-lg bg-gray-950 text-green-300 focus:outline-none focus:ring-2 focus:ring-primary resize-y"
        />
      )}

      {/* ── Form mode ───────────────────────────────────────────────────────── */}
      {!pasteMode && (
        <>
          {/* File info */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                File Path <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={filePath}
                onChange={(e) => setFilePath(e.target.value)}
                placeholder="datalists/7943/file.csv"
                className="w-full font-mono text-xs px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Bucket Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={bucketName}
                onChange={(e) => setBucketName(e.target.value)}
                placeholder="fhin-test-data"
                className="w-full font-mono text-xs px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
          </div>

          {/* Service / timezone / req source */}
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Service</label>
              <select
                value={service}
                onChange={(e) => setService(e.target.value as DataService)}
                className="w-full text-sm px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="gcp">gcp</option>
                <option value="aws">aws</option>
                <option value="azure">azure</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Timezone</label>
              <input
                type="text"
                value={timezone}
                onChange={(e) => setTimezone(e.target.value)}
                className="w-full text-sm px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Request Source</label>
              <input
                type="text"
                value={reqSource}
                onChange={(e) => setReqSource(e.target.value)}
                className="w-full text-sm px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
          </div>

          {/* ── Entity selector ───────────────────────────────────────────── */}
          <div className="pt-2 border-t border-gray-100">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Entity Type <span className="text-red-500">*</span>
            </label>
            {entitiesLoading ? (
              <div className="flex items-center gap-2 text-xs text-gray-400">
                <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading entities…
              </div>
            ) : (
              <div className="flex flex-wrap gap-2">
                {availableEntities.map((e) => (
                  <button
                    key={e}
                    type="button"
                    onClick={() => setSelectedEntity(e)}
                    className={`px-3 py-1.5 text-xs rounded-full border font-medium transition-colors capitalize ${
                      selectedEntity === e
                        ? 'bg-primary text-white border-primary'
                        : 'border-gray-300 text-gray-600 hover:border-primary hover:text-primary'
                    }`}
                  >
                    {e}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Entity metadata (non-customer) */}
          {!isCustomer && (
            <div className="pt-2 border-t border-gray-100">
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Entity ID Column
                <span className="text-gray-400 font-normal ml-1">
                  (CSV column that is the entity's primary key)
                </span>
              </label>
              {localCsvHeaders.length > 0 ? (
                <select
                  value={entityIdCol}
                  onChange={(e) => setEntityIdCol(e.target.value)}
                  className="w-full text-sm px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option value="">— select column —</option>
                  {localCsvHeaders.map((h) => (
                    <option key={h} value={h}>{h}</option>
                  ))}
                </select>
              ) : (
                <input
                  type="text"
                  value={entityIdCol}
                  onChange={(e) => setEntityIdCol(e.target.value)}
                  placeholder="id_column"
                  className="w-full text-sm px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                />
              )}
            </div>
          )}

          {/* ── Column Data Types ─────────────────────────────────────────── */}
          <div className="pt-2 border-t border-gray-100">
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-gray-700">
                Column Data Types
                <span className="text-xs font-normal text-gray-400 ml-1">
                  (required — review and adjust before submitting)
                </span>
              </label>
              <div className="flex items-center gap-3">
                {filePath && bucketName && (
                  <button
                    type="button"
                    onClick={handleFetchHeaders}
                    disabled={headerFetchLoading}
                    className="flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium border border-primary text-primary rounded hover:bg-primary/5 disabled:opacity-50 transition-colors"
                  >
                    {headerFetchLoading
                      ? <><Loader2 className="h-3 w-3 animate-spin" /> Detecting…</>
                      : <><FileText className="h-3 w-3" /> Detect Headers</>}
                  </button>
                )}
                {localCsvHeaders.length > 0 && (
                  <span className="text-xs text-green-600">✓ {localCsvHeaders.length} columns</span>
                )}
              </div>
            </div>
            {headerFetchError && (
              <p className="text-xs text-red-600 mb-2">{headerFetchError}</p>
            )}
            {localCsvHeaders.length > 0 && detectedDataTypes && (
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <div className="bg-gray-50 border-b border-gray-200 px-3 py-2 grid grid-cols-[1fr_8rem] gap-2">
                  <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Column</span>
                  <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Data Type</span>
                </div>
                <div className="divide-y divide-gray-100 max-h-52 overflow-y-auto">
                  {localCsvHeaders.map((header) => {
                    const raw = detectedDataTypes[header]
                    const safeType =
                      raw === 'number' ? 'number'
                      : raw === 'date' ? 'date'
                      : raw === 'boolean' ? 'boolean'
                      : 'string'
                    return (
                      <div key={header} className="grid grid-cols-[1fr_8rem] gap-2 items-center px-3 py-1.5 hover:bg-gray-50">
                        <span className="font-mono text-xs text-gray-700 truncate" title={header}>
                          {header}
                        </span>
                        <select
                          value={safeType}
                          onChange={(e) => handleDataTypeChange(header, e.target.value)}
                          className="text-xs text-gray-900 px-1.5 py-1 border border-gray-300 rounded bg-white focus:outline-none focus:ring-1 focus:ring-primary"
                        >
                          <option value="string">string</option>
                          <option value="number">number</option>
                          <option value="date">date</option>
                          <option value="boolean">boolean</option>
                        </select>
                      </div>
                    )
                  })}
                </div>
              </div>
            )}
          </div>

          {/* ── Field mapping table ───────────────────────────────────────── */}
          <div className="pt-2 border-t border-gray-100">
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-gray-700">
                Field Mapping
              </label>
              {schemasLoading && (
                <span className="flex items-center gap-1 text-xs text-gray-400">
                  <Loader2 className="h-3 w-3 animate-spin" /> Loading schema…
                </span>
              )}
            </div>
            <FieldMappingTable
              csvHeaders={localCsvHeaders}
              schemaFields={schemaFields}
              customerFields={customerSchemaFields}
              showCustomerMapping={!isCustomer}
              value={mappingRows}
              onChange={setMappingRows}
            />
          </div>

          {/* Preview panel */}
          {showPreview && (
            <div className="rounded-lg border border-gray-200 overflow-hidden">
              <div className="flex items-center justify-between px-3 py-2 bg-gray-800 border-b border-gray-700">
                <span className="text-xs text-gray-400 font-mono">Payload Preview</span>
                <button
                  type="button"
                  onClick={() => setShowPreview(false)}
                  className="text-gray-500 hover:text-gray-300"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
              <pre className="bg-gray-950 text-green-300 font-mono text-xs p-4 overflow-x-auto max-h-80 overflow-y-auto">
                {JSON.stringify(generatePayload(), null, 2)}
              </pre>
            </div>
          )}
        </>
      )}

      {/* Error */}
      {error && (
        <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm">
          <AlertCircle className="h-4 w-4 text-red-600 mt-0.5 shrink-0" />
          <p className="text-red-800">{error}</p>
        </div>
      )}

      {/* Submit */}
      <button
        type="button"
        onClick={handleSubmit}
        disabled={loading || !canSubmit}
        className="px-5 py-2 bg-primary text-white text-sm rounded-md hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
      >
        {loading ? (
          <span className="flex items-center gap-2">
            <Loader2 className="h-4 w-4 animate-spin" /> Processing…
          </span>
        ) : (
          'Trigger Ingestion'
        )}
      </button>
      {!pasteMode && !hasDataTypes && filePath && bucketName && (
        <p className="text-xs text-amber-600 mt-1">
          Click "Detect Headers" above to auto-detect column types before submitting.
        </p>
      )}

      {/* Customer ingestion result (dual-call mode) */}
      {customerCallResult && (
        <div>
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
            Customer Ingestion
          </p>
          <IngestResultCard result={customerCallResult} />
        </div>
      )}
    </div>
  )
}

// ── Demo fallbacks ───────────────────────────────────────────────────────────
const DEMO_CUSTOMER_FIELDS: import('../types').SchemaField[] = [
  { name: 'CID', type: 'string' },
  { name: 'CONTACT_EMAIL', type: 'string' },
  { name: 'CONTACT_MOBILE', type: 'string' },
  { name: 'FIRST_NAME', type: 'string' },
  { name: 'LAST_NAME', type: 'string' },
  { name: 'DATE_OF_BIRTH', type: 'date' },
  { name: 'GENDER', type: 'string' },
  { name: 'CITY', type: 'string' },
  { name: 'STATE', type: 'string' },
  { name: 'COUNTRY', type: 'string' },
]

const DEMO_ENTITY_FIELDS: import('../types').SchemaField[] = [
  { name: 'id_orig', type: 'string' },
  { name: 'policy_number', type: 'string' },
  { name: 'start_date', type: 'date' },
  { name: 'end_date', type: 'date' },
  { name: 'status', type: 'string' },
]

// ── IngestResultCard ─────────────────────────────────────────────────────────
export function IngestResultCard({ result }: { result: IngestResponse }) {
  const accepted = result.status === 'ACCEPTED'
  return (
    <div
      className={`rounded-lg border px-4 py-3 text-sm ${
        accepted
          ? 'bg-green-50 border-green-200'
          : 'bg-red-50 border-red-200'
      }`}
    >
      <div className="flex items-center gap-2 mb-1">
        {accepted ? (
          <CheckCircle2 className="h-4 w-4 text-green-600 shrink-0" />
        ) : (
          <AlertCircle className="h-4 w-4 text-red-600 shrink-0" />
        )}
        <span className={`font-semibold ${accepted ? 'text-green-800' : 'text-red-800'}`}>
          {result.status}
        </span>
        <span className="text-gray-500 text-xs">#{result.request_id}</span>
      </div>
      <p className={accepted ? 'text-green-700' : 'text-red-700'}>{result.message}</p>
      {result.validation_summary && (
        <div className="mt-2 text-xs text-gray-500 flex gap-4">
          <span>{result.validation_summary.headers_validated} headers validated</span>
          {result.validation_summary.total_rows_in_file != null && (
            <span>{result.validation_summary.total_rows_in_file.toLocaleString()} rows</span>
          )}
        </div>
      )}
      {result.errors?.length ? (
        <ul className="mt-2 space-y-1">
          {result.errors.map((e, i) => (
            <li key={i} className="text-xs text-red-700">
              <span className="font-mono bg-red-100 px-1 rounded mr-1">{e.code}</span>
              {e.message}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  )
}

